import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
// At most 6 decimals: a display figure never has more, and the formatter
// throws past 100.
const FIGURE_RE = /^([^\d\-−]{0,3})([-−]?)(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,6}))?([^\d]{0,6})$/;
/**
 * The number inside a formatted figure, or null when the text is not one
 * figure: a date, a range, a year or words.
 */ export function splitFigure(text) {
    const m = FIGURE_RE.exec(text.trim());
    if (!m) return null;
    const [, prefix, sign, whole, frac = '', suffix] = m;
    const value = Number(`${sign ? '-' : ''}${whole.replace(/,/g, '')}.${frac || '0'}`);
    if (!Number.isFinite(value)) return null;
    // A bare year reads as a date, not an amount.
    if (!prefix && !suffix && !frac && !whole.includes(',') && value >= 1900 && value <= 2100) {
        return null;
    }
    return {
        prefix,
        value,
        suffix,
        decimals: frac.length,
        grouped: whole.includes(',')
    };
}
function formatFigure(f, n) {
    const abs = Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: f.decimals,
        maximumFractionDigits: f.decimals,
        useGrouping: f.grouped || Math.abs(f.value) >= 10000
    });
    return `${f.prefix}${n < 0 ? '-' : ''}${abs}${f.suffix}`;
}
function prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
const easeOut = (t)=>1 - (1 - t) ** 3;
/**
 * A figure that counts up to its value the first time it scrolls into view,
 * and tweens from the old value when it changes (a bound input moved).
 * Screen readers get the final value only; reduced motion shows it at once.
 */ export function SpecraCountUp({ value, duration = 700 }) {
    const figure = React.useMemo(()=>splitFigure(value), [
        value
    ]);
    const ref = React.useRef(null);
    const shownRef = React.useRef(null);
    // Starts at zero (held until on screen) unless motion is reduced.
    const [text, setText] = React.useState(()=>figure && !prefersReducedMotion() ? formatFigure(figure, 0) : value);
    const [seen, setSeen] = React.useState(false);
    React.useEffect(()=>{
        const el = ref.current;
        if (seen || !el) return;
        if (typeof IntersectionObserver === 'undefined') {
            setSeen(true);
            return;
        }
        const io = new IntersectionObserver((entries)=>{
            if (entries.some((e)=>e.isIntersecting)) {
                setSeen(true);
                io.disconnect();
            }
        });
        io.observe(el);
        return ()=>io.disconnect();
    }, [
        seen
    ]);
    React.useEffect(()=>{
        if (!figure || prefersReducedMotion()) {
            setText(value);
            shownRef.current = figure && {
                n: figure.value,
                suffix: figure.suffix
            };
            return;
        }
        if (!seen) {
            // Hold at zero until it is on screen, so the count is seen.
            setText(formatFigure(figure, 0));
            return;
        }
        const prev = shownRef.current;
        // A unit change ($999K → $1.2M) swaps rather than tweening across scales.
        const from = prev && prev.suffix === figure.suffix ? prev.n : prev ? figure.value : 0;
        if (from === figure.value) {
            setText(value);
            shownRef.current = {
                n: figure.value,
                suffix: figure.suffix
            };
            return;
        }
        let raf = 0;
        const start = performance.now();
        const tick = (now)=>{
            const t = Math.min(1, (now - start) / duration);
            const n = from + (figure.value - from) * easeOut(t);
            shownRef.current = {
                n,
                suffix: figure.suffix
            };
            if (t < 1) {
                setText(formatFigure(figure, n));
                raf = requestAnimationFrame(tick);
            } else {
                setText(value);
            }
        };
        raf = requestAnimationFrame(tick);
        return ()=>cancelAnimationFrame(raf);
    }, [
        figure,
        value,
        seen,
        duration
    ]);
    if (!figure || text === value) return /*#__PURE__*/ _jsx("span", {
        ref: ref,
        children: value
    });
    return /*#__PURE__*/ _jsxs("span", {
        ref: ref,
        className: "tabular-nums",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "sr-only",
                children: value
            }),
            /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                children: text
            })
        ]
    });
}
